import unifiedApi from './unifiedApi';

// Web Push helpers for chat notifications. iOS only supports Web Push for apps added to the
// Home Screen (iOS 16.4+); permission must be requested from a tap.
export const SERVICE_WORKER_URL = '/push-sw.js';

export const isPushSupported = () => typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

export const isIos = () => typeof navigator !== 'undefined'
  && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const isStandalone = () => typeof window !== 'undefined'
  && ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true);

/** iPhone/iPad in Safari: notifications only work once the app is added to the Home Screen. */
export const needsInstallForPush = () => isIos() && !isStandalone();

export const getNotificationPermission = () => (typeof window !== 'undefined' && 'Notification' in window
  ? window.Notification.permission
  : 'unsupported');

export const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const sameKey = (applicationServerKey, vapidPublicKey) => {
  if (!applicationServerKey) return false;
  const current = new Uint8Array(applicationServerKey);
  const expected = urlBase64ToUint8Array(vapidPublicKey);
  return current.length === expected.length && current.every((byte, i) => byte === expected[i]);
};

const getRegistration = async () => {
  if (!isPushSupported()) return null;
  return navigator.serviceWorker.getRegistration('/');
};

export const getCurrentSubscription = async () => {
  const registration = await getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
};

/**
 * Asks permission (must be called from a tap), subscribes this device, and saves it for the member.
 * Returns the resulting permission: 'granted', 'denied', 'default', or 'unsupported'.
 */
export const enablePushOnDevice = async (vapidPublicKey) => {
  if (!isPushSupported()) return 'unsupported';
  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') return permission;

  await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription.options?.applicationServerKey, vapidPublicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }
  await unifiedApi.saveChatPushSubscription(subscription.toJSON());
  return 'granted';
};

/** Unsubscribes this device and forgets it on the server. */
export const disablePushOnDevice = async () => {
  const subscription = await getCurrentSubscription();
  if (!subscription) return;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  try {
    await unifiedApi.removeChatPushSubscription(endpoint);
  } catch {
    // The server drops it on the next failed delivery anyway.
  }
};

/** Re-saves this device's subscription (e.g. a different member signed in on the same browser). */
export const syncPushSubscription = async () => {
  if (getNotificationPermission() !== 'granted') return;
  const subscription = await getCurrentSubscription();
  if (subscription) await unifiedApi.saveChatPushSubscription(subscription.toJSON());
};

/**
 * On sign-out, stop this member's notifications on a possibly shared device. The browser subscription is
 * revoked locally (so even if the server call fails, the push service rejects further sends and the server
 * drops the mapping), and the server mapping is removed best-effort. The member turns notifications back on
 * from chat settings after signing in again.
 */
export const forgetPushDeviceOnLogout = async (timeoutMs = 2000) => {
  try {
    const subscription = await getCurrentSubscription();
    if (!subscription) return;
    const { endpoint } = subscription;
    await Promise.race([
      Promise.allSettled([
        subscription.unsubscribe(),
        unifiedApi.removeChatPushSubscription(endpoint),
      ]),
      new Promise((resolve) => { setTimeout(resolve, timeoutMs); }),
    ]);
  } catch {
    // Never block sign-out.
  }
};

export const clearAppBadge = () => {
  if (typeof navigator !== 'undefined' && navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
};

/** Removes this chat's notifications from the notification tray once it's opened. */
export const closeChannelNotifications = async (channelId) => {
  try {
    const registration = await getRegistration();
    const notifications = registration ? await registration.getNotifications({ tag: `chat-${channelId}` }) : [];
    notifications.forEach((n) => n.close());
  } catch {
    // Not supported everywhere.
  }
};
